import { useCallback } from 'react';
import ApiConstants from '../utils/ApiConstants';
import { scrollToElement } from '../utils/utils';
import { useAuthenticatedApiFetch } from './useAuthenticatedApiFetch';
import { useMsal } from "@azure/msal-react";
import { useAppNavigation } from "../hooks/useAppNavigation";

const useNavigateToQuestion = () => {
  const { instance } = useMsal();
  const authenticatedApiFetch = useAuthenticatedApiFetch(instance);
  const { goToError } = useAppNavigation();

  const navigateToTarget = useCallback(
    async (
      product,
      client,
      compositionType, // "QUESTION" or "DECISION"
      code, // questionCode or decisionCode
      setFirstLevelArea,
      setSubArea,
      setSecondLevelArea,
      setHasNext,
      setHasPrevious,
      setPreviousFirstLevelArea,
      setPreviousSecondLevelArea,
      setPreviousSubArea,
      firstLevelArea,
      secondLevelArea,
      subArea,
      uiRenderType,
      inputRefs,
    ) => {
      try {
        await instance.initialize();

        let endpoint;
        if (compositionType === "QUESTION") {
          endpoint = ApiConstants.getNavigationToQuestion(product, client, code);
        } else if (compositionType === "DECISION") {
          endpoint = ApiConstants.getNavigationToDecision(product, client, code);
        } else {
          console.error(`Invalid compositionType: ${compositionType}`);
          goToError();
          return;
        }

        const url = `${ApiConstants.BASE_URL}${endpoint}`;
        const result = await authenticatedApiFetch(url);

        // Handle HTTP errors (404, 500, etc.)
        if (!result || result.status >= 400) {
          console.error(`Navigation failed with status: ${result?.status}`);
          goToError();
          return;
        }

        const data = result.data;
        if (!data) {
          console.error("No data returned from navigation endpoint.");
          goToError();
          return;
        }

        const {
          firstLevelAreaId,
          secondLevelAreaId,
          productSubAreaId: newProductSubAreaId,
          hasNext,
          hasPrevious,
          navigateToId,
        } = data;

        if (!navigateToId) {
          console.error("navigateToId missing from response.");
          goToError();
          return;
        }

        // Save previous areas
        setPreviousFirstLevelArea(firstLevelArea);
        setPreviousSecondLevelArea(secondLevelArea);
        setPreviousSubArea(subArea);

        // Update areas and navigation flags
        setFirstLevelArea(firstLevelAreaId);
        setSubArea(newProductSubAreaId);
        setSecondLevelArea(secondLevelAreaId || null);
        setHasNext(hasNext);
        setHasPrevious(hasPrevious);

        // Scroll to new element
        const newGoToId = `go_to_${navigateToId}`;
        scrollToElement(newGoToId, inputRefs);

      } catch (error) {
        console.error("Error navigating:", error);
        goToError();
      }
    },
    [instance, authenticatedApiFetch, goToError]
  );

  return navigateToTarget;
};

export default useNavigateToQuestion;
